import { ApiError } from "@agentry/api-client";
import type {
	ChatMessage,
	CreateArtifactRequest,
	GuidedQuestion,
} from "@agentry/zerovia-contracts";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { useApi } from "../../app/ApiProvider";
import { useAuth, useScope } from "../../app/AuthProvider";
import { useAction, useAsync } from "../../app/useAsync";
import {
	Badge,
	Button,
	Callout,
	Card,
	cx,
	LinkButton,
	PageHeader,
	StatusBadge,
	TextArea,
	waitReasonLabel,
} from "../../ui/primitives";
import {
	EmptyState,
	ErrorState,
	isNotFound,
	LoadingState,
	NotFoundState,
	recoveryFor,
} from "../../ui/states";
import { PACK_HEARING_UCS_ID, packHearing } from "./pack-hearing";

function isInternalSessionTitle(title: string): boolean {
	return /^\[(?:WO|DESIGN):/.test(title);
}

export function SessionListPage() {
	const { client } = useApi();
	const scope = useScope();
	const data = useAsync(
		() => client.listSessions(scope),
		[client, scope.tenantId, scope.projectId],
	);
	const endUserSessions =
		data.status === "ready"
			? data.data.items.filter((item) => !isInternalSessionTitle(item.title))
			: [];

	return (
		<>
			<PageHeader
				eyebrow="会話履歴"
				title="これまでの依頼"
				description="途中で止まっている依頼も、続きから再開できます。"
				actions={<LinkButton to="/app/agents">新しく依頼する</LinkButton>}
			/>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" &&
				(endUserSessions.length === 0 ? (
					<EmptyState
						title="まだ依頼がありません"
						description="Agentを選ぶと質問が始まります。"
						action={
							<LinkButton to="/app/agents" size="sm">
								Agentを探す
							</LinkButton>
						}
					/>
				) : (
					<ul className="space-y-2">
						{endUserSessions.map((item) => (
							<li key={item.id}>
								<Link
									to={`/app/sessions/${item.id}`}
									className="ag-card flex flex-wrap items-center justify-between gap-3 px-4 py-3 transition-colors hover:border-brand-300"
								>
									<div className="min-w-0">
										<p className="truncate text-[15px] font-semibold text-ink-900">{item.title}</p>
										<p className="mt-0.5 truncate text-[13px] text-ink-500">
											{item.businessPurpose || "目的未設定"}
										</p>
									</div>
									<div className="flex shrink-0 items-center gap-2">
										<Badge tone="neutral">
											{item.sessionMode === "GUIDED_WORKFLOW" ? "質問形式" : "自由入力"}
										</Badge>
										<StatusBadge status={item.status} />
									</div>
								</Link>
							</li>
						))}
					</ul>
				))}
		</>
	);
}

export function ChatPage() {
	const { sessionId = "" } = useParams();
	const { client } = useApi();
	const { session: auth } = useAuth();
	const scope = useScope();
	const [draft, setDraft] = useState("");
	const [refreshSeq, setRefreshSeq] = useState(0);
	const bottomRef = useRef<HTMLDivElement>(null);

	const data = useAsync(async () => {
		const [detail, catalog, routes] = await Promise.all([
			client.getSession(scope, sessionId),
			client.getCatalog(scope),
			client.getSessionAuthRoutes(scope, sessionId).catch(() => []),
		]);
		return { detail, catalog, routes };
	}, [client, scope.tenantId, scope.projectId, sessionId, refreshSeq]);

	const send = useAction(
		async (input: {
			content: string;
			startWorkflow: boolean;
			interviewAnswers?: Record<string, string>;
			intake?: CreateArtifactRequest;
			executionId?: string;
			answers?: Record<string, string>;
			unknown?: string[];
			attachmentArtifactIds?: string[];
			siteId?: string;
		}) => {
			// 再読込をまたいでも同一操作は同じKey、同じ位置からの別操作は別Keyになる。
			const persistedUserMessages =
				data.data?.detail.messages.filter((message) => message.role === "USER").length ?? 0;
			const key = chatIdempotencyKey(sessionId, persistedUserMessages, input.content);
			const intakeArtifact = input.intake ? await client.createArtifact(scope, input.intake) : null;
			const chat = await client.sendChat(
				scope,
				sessionId,
				{
					content: input.content,
					taskProfile: {},
					startWorkflow: input.startWorkflow,
					interviewAnswers: input.interviewAnswers,
				},
				key,
			);
			// Chat自体が受理された時点でKeyを進める。後続のUSER_INPUT確定だけが
			// 失敗して再送されても、異なる本文で同じ冪等Keyを再利用しない。
			setRefreshSeq((n) => n + 1);
			if (intakeArtifact) {
				const executionId = input.executionId ?? chat.executionId;
				if (!executionId) {
					throw new Error("実行が開始されなかったため、回答を確定できませんでした。");
				}
				await client.submitUserInput(
					scope,
					executionId,
					{
						intakeArtifactId: intakeArtifact.id,
						answers: input.answers ?? {},
						unknown: input.unknown ?? [],
						attachmentArtifactIds: input.attachmentArtifactIds ?? [],
						siteId: input.siteId ?? "default",
					},
					`${key}-user-input`,
				);
				// sendChat直後の再取得はresolve前のWAITINGを返し得る。resolve後にも
				// 再取得して、S1へ進んだ状態を利用者へ即時に反映する。
				setRefreshSeq((n) => n + 1);
			}
			return chat;
		},
	);
	const upload = useAction(async (input: { file: File; ownerUserId: string }) => {
		const { file, ownerUserId } = input;
		if (file.type !== "application/pdf" && !file.type.startsWith("image/")) {
			throw new Error("PDFまたは画像だけを添付できます。");
		}
		return client.createArtifact(scope, {
			projectId: scope.projectId,
			workspaceId: null,
			sessionId,
			executionId: null,
			nodeExecutionId: null,
			ownerUserId,
			generatedByActorType: "USER",
			generatedByActorId: ownerUserId,
			authenticationProfileRef: null,
			name: file.name,
			type: "source_attachment",
			mimeType: file.type,
			content: await readAsDataUrl(file),
			supersedesArtifactId: null,
		});
	});

	// biome-ignore lint/correctness/useExhaustiveDependencies: 新規Message到着時のみ末尾へ送る
	useEffect(() => {
		// jsdom等 scrollIntoView を持たない環境で画面全体を落とさない
		bottomRef.current?.scrollIntoView?.({ block: "end" });
	}, [data.status, data.data?.detail.messages.length]);

	if (data.status === "loading") return <LoadingState label="やりとりを読み込んでいます" />;
	if (data.status === "error") {
		if (isNotFound(data.error)) {
			return <NotFoundState detail="この依頼にはアクセスできません。" />;
		}
		return <ErrorState error={data.error} onRetry={data.reload} />;
	}

	const { detail, catalog, routes } = data.data;
	const ucs = catalog.ucs.find((u) => u.id === detail.session.ucsId);
	const pack = catalog.verticalPackages.find((v) => v.key === ucs?.verticalKey);
	const answered = detail.messages.filter((m) => m.role === "USER").length;
	const usesPackHearing = detail.session.ucsId === PACK_HEARING_UCS_ID;
	const questions = usesPackHearing ? packHearing.questions : (pack?.guidedQuestions ?? []);
	const currentQuestion: GuidedQuestion | undefined = questions[answered];
	const guided = detail.session.sessionMode === "GUIDED_WORKFLOW";
	const activeExecution = detail.executions.at(-1) ?? null;

	const interview = collectInterview(questions, detail.messages);
	const selectedSite = interview.answers.site;
	const currentDefault =
		currentQuestion?.key === "audience" && selectedSite
			? packHearing.customPatchDefaults[`audience.${selectedSite}`]
			: undefined;

	async function submit(content: string) {
		const trimmed = content.trim();
		if (!trimmed) return;
		const submittedDraft = draft;
		const restoreTypedDraftOnFailure = submittedDraft.trim() === trimmed;
		// POSTが遅い場合も送信を受け付けたことが直ちに分かるよう、入力欄は先に空にする。
		// 失敗時は、利用者が送信中に新しい入力を始めていない場合だけ元の文面を戻す。
		setDraft("");
		const explicitStart = trimmed === "実行を開始" && answered >= questions.length;
		const completesHearing =
			guided && questions.length > 0 && (answered + 1 >= questions.length || explicitStart);
		const finalInterview = collectInterview(questions, [
			...detail.messages,
			{ role: "USER", content: trimmed },
		]);
		const intake =
			usesPackHearing && completesHearing
				? ({
						projectId: scope.projectId,
						workspaceId: null,
						sessionId,
						executionId: null,
						nodeExecutionId: null,
						ownerUserId: auth?.user.id ?? detail.session.ownerUserId,
						generatedByActorType: "USER",
						generatedByActorId: auth?.user.id ?? detail.session.ownerUserId,
						authenticationProfileRef: null,
						name: "intake.json",
						type: "intake",
						mimeType: "application/json",
						content: JSON.stringify({
							answers: finalInterview.answers,
							unknown: finalInterview.unknown,
							attachmentArtifactIds: detail.artifacts
								.filter((artifact) => artifact.type === "source_attachment")
								.map((artifact) => artifact.id),
							customPatchDefaults: packHearing.customPatchDefaults,
						}),
						supersedesArtifactId:
							detail.artifacts.find((artifact) => artifact.type === "intake")?.id ?? null,
					} satisfies CreateArtifactRequest)
				: undefined;
		const result = await send.run({
			content: trimmed,
			// Real APIは明示された時だけWorkflowを開始する。最後のGuided回答と
			// 同じ冪等リクエストで開始し、回答だけ保存される中途半端な状態を作らない。
			startWorkflow: completesHearing && !activeExecution,
			interviewAnswers: completesHearing ? finalInterview.answers : undefined,
			intake,
			executionId: activeExecution?.id,
			answers: finalInterview.answers,
			unknown: finalInterview.unknown,
			attachmentArtifactIds: detail.artifacts
				.filter((artifact) => artifact.type === "source_attachment")
				.map((artifact) => artifact.id),
			siteId: finalInterview.answers.site ?? "default",
		});
		if (!result && restoreTypedDraftOnFailure) {
			setDraft((current) => (current === "" ? submittedDraft : current));
		}
		if (!result && intake) data.reload();
	}

	return (
		<>
			<PageHeader
				eyebrow={ucs?.displayName ?? detail.session.ucsId}
				title={detail.session.title}
				description={detail.session.businessPurpose || undefined}
				actions={
					activeExecution && (
						<LinkButton to={`/app/progress/${activeExecution.id}`} variant="secondary">
							進捗を見る
						</LinkButton>
					)
				}
			/>

			<div className="grid gap-4 lg:grid-cols-[1fr_320px]">
				<div className="flex min-h-[520px] flex-col rounded-[14px] border border-ink-200 bg-white">
					{guided && questions.length > 0 && (
						<div className="border-b border-ink-100 px-4 py-2.5">
							<div className="flex items-center justify-between text-[12px] text-ink-500">
								<span>
									質問 {Math.min(answered + 1, questions.length)} / {questions.length}
								</span>
								<span>
									{Math.round((Math.min(answered, questions.length) / questions.length) * 100)}%
								</span>
							</div>
							<div className="mt-1 h-1.5 overflow-hidden rounded-full bg-ink-100">
								<div
									className="h-full rounded-full bg-brand-600 transition-[width]"
									style={{
										width: `${Math.round((Math.min(answered, questions.length) / questions.length) * 100)}%`,
									}}
								/>
							</div>
						</div>
					)}

					<div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
						{detail.messages.length === 0 ? (
							<EmptyState title="まだやりとりがありません" />
						) : (
							detail.messages.map((message) => <Bubble key={message.id} message={message} />)
						)}
						<div ref={bottomRef} />
					</div>

					<div className="border-t border-ink-100 px-4 py-3">
						{currentQuestion && usesPackHearing && (
							<div className="mb-3 rounded-xl bg-brand-50 px-3 py-2.5">
								<p className="text-[14px] font-semibold text-ink-900">{currentQuestion.prompt}</p>
								{currentDefault && (
									<p className="mt-1 text-[12.5px] text-ink-600">
										既定値: {currentDefault}。必要なら修正してください。
									</p>
								)}
							</div>
						)}
						{usesPackHearing &&
							!currentQuestion &&
							activeExecution?.waitReason === "USER_INPUT" && (
								<Button
									type="button"
									size="sm"
									variant="secondary"
									disabled={send.pending}
									onClick={() => void submit("実行を開始")}
								>
									実行を開始
								</Button>
							)}
						{currentQuestion && currentQuestion.options.length > 0 && (
							<div className="mb-2.5 flex flex-wrap gap-1.5">
								{currentQuestion.options.map((option) => (
									<button
										key={option}
										type="button"
										disabled={send.pending}
										onClick={() => void submit(option)}
										className="rounded-full border border-brand-300 bg-white px-3 py-1 text-[13px] text-brand-700 transition-colors hover:bg-brand-50 disabled:opacity-50"
									>
										{option}
									</button>
								))}
							</div>
						)}

						<form
							onSubmit={(event) => {
								event.preventDefault();
								void submit(draft);
							}}
						>
							<TextArea
								rows={2}
								value={draft}
								onChange={(event) => setDraft(event.target.value)}
								placeholder={
									currentQuestion
										? "回答を入力（選択肢から選んでもかまいません）"
										: "メッセージを入力"
								}
								aria-label="メッセージを入力"
								onKeyDown={(event) => {
									// IME変換中のEnterで送信しない
									if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
										event.preventDefault();
										void submit(draft);
									}
								}}
							/>
							<div className="mt-2 flex items-center justify-between gap-2">
								<span className="text-[12px] text-ink-400">⌘/Ctrl + Enter で送信</span>
								<Button
									type="submit"
									size="sm"
									disabled={send.pending || draft.trim().length === 0}
								>
									{send.pending ? "送信中…" : "送信"}
								</Button>
							</div>
						</form>
						{currentQuestion && usesPackHearing && (
							<div className="mt-2 flex flex-wrap gap-2">
								<Button
									type="button"
									size="sm"
									variant="secondary"
									disabled={send.pending}
									onClick={() => void submit("わからない")}
								>
									わからない
								</Button>
								<label className="inline-flex cursor-pointer items-center rounded-xl border border-ink-200 px-3 text-[13px] font-bold text-ink-700">
									{upload.pending ? "添付中…" : "PDF・画像を添付"}
									<input
										type="file"
										className="sr-only"
										accept="application/pdf,image/*"
										disabled={upload.pending}
										onChange={async (event) => {
											const file = event.currentTarget.files?.[0];
											if (
												file &&
												(await upload.run({
													file,
													ownerUserId: auth?.user.id ?? detail.session.ownerUserId,
												}))
											) {
												setRefreshSeq((n) => n + 1);
											}
										}}
									/>
								</label>
							</div>
						)}

						{send.error != null && (
							<div
								role="alert"
								className="mt-2 rounded-lg border border-danger-500/40 bg-danger-100 px-3 py-2"
							>
								<p className="text-[13px] font-bold text-danger-600">
									{recoveryFor(send.error).title}
								</p>
								<p className="text-[12.5px] text-ink-700">{recoveryFor(send.error).hint}</p>
								{send.error instanceof ApiError && (
									<p className="mt-1 text-[12.5px] text-ink-700">
										{send.error.code}: {send.error.message}
									</p>
								)}
							</div>
						)}
						{upload.error != null && (
							<p role="alert" className="mt-2 text-[12.5px] text-danger-600">
								{upload.error instanceof Error ? upload.error.message : "添付に失敗しました。"}
							</p>
						)}
					</div>
				</div>

				<div className="space-y-4">
					{activeExecution && (
						<Card title="いまの状況">
							<div className="flex items-center gap-2">
								<StatusBadge status={activeExecution.status} />
								{activeExecution.waitReason && (
									<span className="text-[13px] text-ink-600">
										{waitReasonLabel(activeExecution.waitReason)}
									</span>
								)}
							</div>
							<LinkButton
								to={`/app/progress/${activeExecution.id}`}
								variant="secondary"
								size="sm"
								className="mt-3 w-full"
							>
								進捗の詳細
							</LinkButton>
						</Card>
					)}

					{detail.artifacts.length > 0 && (
						<Card title="この依頼の成果物">
							<ul className="space-y-1.5">
								{detail.artifacts.map((artifact) => (
									<li key={artifact.id}>
										<Link
											to={`/app/artifacts/${artifact.id}`}
											className="flex items-center justify-between gap-2 text-[13.5px] text-ink-800 hover:text-brand-600"
										>
											<span className="truncate">{artifact.name}</span>
											<Badge tone="neutral">v{artifact.version}</Badge>
										</Link>
									</li>
								))}
							</ul>
						</Card>
					)}

					<Card title="使うAI接続" description="どのAI契約が使われるかだけを表示します">
						{routes.length === 0 ? (
							<EmptyState title="利用可能な接続がありません" />
						) : (
							<div className="space-y-3">
								{routes.map((route) => (
									<div key={route.routeKey}>
										<p className="text-[13px] font-semibold text-ink-800">{route.providerLabel}</p>
										<p className="text-[11.5px] text-ink-500">{route.taskTypes.join(" / ")}</p>
										<ul className="mt-1 space-y-1">
											{route.candidates.map((candidate) => (
												<li
													key={`${route.routeKey}-${candidate.label}`}
													className="flex items-start justify-between gap-2 text-[12.5px]"
												>
													<span
														className={
															candidate.state === "UNAVAILABLE" ? "text-ink-400" : "text-ink-700"
														}
													>
														{candidate.label}
														{candidate.unavailableReason && (
															<span className="block text-[11.5px] text-ink-400">
																{candidate.unavailableReason}
															</span>
														)}
													</span>
													<Badge
														tone={
															candidate.state === "PRIMARY"
																? "success"
																: candidate.state === "FALLBACK"
																	? "neutral"
																	: "warning"
														}
													>
														{candidate.state === "PRIMARY"
															? "優先"
															: candidate.state === "FALLBACK"
																? "予備"
																: "利用不可"}
													</Badge>
												</li>
											))}
										</ul>
									</div>
								))}
							</div>
						)}
						<LinkButton
							to="/app/settings/credentials"
							variant="secondary"
							size="sm"
							className="mt-3 w-full"
						>
							AI接続設定を開く
						</LinkButton>
					</Card>

					<Card title="この依頼について">
						<dl className="space-y-2 text-[13px]">
							<div>
								<dt className="text-ink-500">担当者（所有者）</dt>
								<dd className="text-ink-900">
									{detail.session.ownerUserId === auth?.user.id
										? `${auth?.user.name}（あなた）`
										: detail.session.ownerUserId}
								</dd>
							</div>
							<div>
								<dt className="text-ink-500">進め方</dt>
								<dd className="text-ink-900">{guided ? "質問形式（Guided）" : "自由入力"}</dd>
							</div>
							<div>
								<dt className="text-ink-500">取り扱い区分</dt>
								<dd className="text-ink-900">{detail.session.securityClassification}</dd>
							</div>
						</dl>
					</Card>
				</div>
			</div>
		</>
	);
}

function Bubble({ message }: { message: ChatMessage }) {
	if (message.role === "SYSTEM") {
		return (
			<Callout tone="neutral">
				<p className="text-[13px]">{message.content}</p>
			</Callout>
		);
	}
	const isAgent = message.role === "AGENT";
	return (
		<div className={isAgent ? "flex gap-2" : "flex justify-end"}>
			{isAgent && (
				<span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-600 text-[11px] font-bold text-white">
					A
				</span>
			)}
			<div className={cx("max-w-xl", !isAgent && "text-right")}>
				<p
					data-message-role={message.role.toLowerCase()}
					className={cx(
						"inline-block whitespace-pre-wrap px-3 py-2 text-left text-[14px] leading-relaxed",
						isAgent
							? "rounded-2xl rounded-tl-sm bg-ink-100 text-ink-800"
							: "rounded-2xl rounded-tr-sm bg-brand-600 text-white",
					)}
				>
					{message.content}
				</p>
			</div>
		</div>
	);
}

/** Chat内で参照するMessage一覧のMemo化ヘルパ（大量Message時の再描画を抑える）。 */
export function useVisibleMessages(messages: ChatMessage[], limit = 200): ChatMessage[] {
	return useMemo(
		() => (messages.length <= limit ? messages : messages.slice(-limit)),
		[messages, limit],
	);
}

function collectInterview(
	questions: GuidedQuestion[],
	messages: Array<Pick<ChatMessage, "role" | "content">>,
): { answers: Record<string, string>; unknown: string[] } {
	const userMessages = messages.filter((message) => message.role === "USER");
	const answers: Record<string, string> = {};
	const unknown: string[] = [];
	for (const [index, message] of userMessages.entries()) {
		const key = questions[index]?.key;
		if (!key) continue;
		if (message.content === "わからない") unknown.push(key);
		else answers[key] = message.content;
	}
	return { answers, unknown };
}

function readAsDataUrl(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.addEventListener("load", () => resolve(String(reader.result)));
		reader.addEventListener("error", () =>
			reject(reader.error ?? new Error("添付に失敗しました。")),
		);
		reader.readAsDataURL(file);
	});
}

export function chatIdempotencyKey(sessionId: string, sequence: number, content: string): string {
	let hash = 2_166_136_261;
	for (let index = 0; index < content.length; index += 1) {
		hash ^= content.charCodeAt(index);
		hash = Math.imul(hash, 16_777_619);
	}
	return `chat-${sessionId}-${sequence}-${(hash >>> 0).toString(36)}`;
}
